import { Injectable } from '@angular/core';
import { isNil, Nillable } from '@web/utils';
import { ColumnKey } from '../types/column-key';
import { CollectionFilterKey, RequiredMethodCallException } from '@web/common';
import { TableColumnType } from '../enums/table-column-type';
import { TableColumn } from '../models/table-column';

@Injectable({
  providedIn: 'root',
})
export class TableColumnBuilder<Item, Filter> {
  private _key: Nillable<ColumnKey<Item>>;
  private _sortKey: Nillable<string>;
  private _filterKey: Nillable<CollectionFilterKey<Filter>>;
  private _filterable: Nillable<boolean>;
  private _sortable: Nillable<boolean>;
  private _label: Nillable<string>;
  private _type: Nillable<TableColumnType>;
  private _width: Nillable<string>;
  private _currency: Nillable<string>;
  private _frozen: Nillable<boolean>;

  public constructor() {
    this.reset();
  }

  public reset(): this {
    this._key = null;
    this._label = null;
    this._sortKey = null;
    this._filterKey = null;
    this._sortable = null;
    this._filterable = null;
    this._type = null;
    this._width = null;
    this._currency = null;
    this._frozen = null;

    return this;
  }

  public initSelection(): this {
    return this.key('selection').type(TableColumnType.Select).width('5rem');
  }

  public initOrdinal(): this {
    return this.key('ordinal')
      .label('#')
      .type(TableColumnType.Ordinal)
      .width('5rem');
  }

  public initText(key: ColumnKey<Item>): this {
    return this.key(key).type(TableColumnType.Text).width('16rem');
  }

  public initCurrency(key: ColumnKey<Item>): this {
    return this.key(key)
      .type(TableColumnType.Currency)
      .width('12rem')
      .currency('zł');
  }

  public initBoolean(key: ColumnKey<Item>): this {
    return this.key(key).type(TableColumnType.Boolean).width('16rem');
  }

  public initDate(key: ColumnKey<Item>): this {
    return this.key(key).type(TableColumnType.Date).width('18rem');
  }

  public initDateTime(key: ColumnKey<Item>): this {
    return this.key(key).type(TableColumnType.DateTime).width('18rem');
  }

  public initNumber(key: ColumnKey<Item>): this {
    return this.key(key).type(TableColumnType.Number).width('16rem');
  }

  public initCustom(key: ColumnKey<Item>): this {
    return this.key(key).type(TableColumnType.Custom);
  }

  public key(key: ColumnKey<Item>): this {
    this._key = key;

    return this;
  }

  public width(width: string): this {
    this._width = width;

    return this;
  }

  public currency(currency: string): this {
    this._currency = currency;

    return this;
  }

  public frozen(frozen: boolean): this {
    this._frozen = frozen;

    return this;
  }

  public filterable(filterable: boolean): this {
    this._filterable = filterable;

    return this;
  }

  public sortable(sortable: boolean): this {
    this._sortable = sortable;

    return this;
  }

  public sortKey(key: string): this {
    this._sortKey = key;

    return this.sortable(true);
  }

  public filterKey(key: CollectionFilterKey<Filter>): this {
    this._filterKey = key;

    return this.filterable(true);
  }

  public label(label: string): this {
    this._label = label;

    return this;
  }

  public type(type: TableColumnType): this {
    this._type = type;

    return this;
  }

  public build(): TableColumn<Item, Filter> {
    this.configureDefaultSortable();
    this.configureDefaultFilterable();
    this.configureDefaultFrozen();
    this.configureDefaultType();

    this.requireKey();
    this.requireWidth();
    this.requireSortKey();
    this.requireFilterKey();

    if (this.isCurrency()) {
      this.requireCurrency();
    }

    const column = new TableColumn<Item, Filter>({
      key: this._key!,
      type: this._type!,
      sortable: this._sortable!,
      filterable: this._filterable!,
      width: this._width!,
      sortKey: this._sortKey,
      filterKey: this._filterKey,
      label: this._label,
      currency: this._currency,
      frozen: this._frozen,
    });

    this.reset();

    return column;
  }

  private requireCurrency(): void {
    if (!isNil(this._currency)) {
      return;
    }

    throw new RequiredMethodCallException(this.currency.name);
  }

  private requireKey(): void {
    if (!isNil(this._key)) {
      return;
    }

    throw new RequiredMethodCallException(this.key.name);
  }

  private requireWidth(): void {
    if (!isNil(this._width)) {
      return;
    }

    throw new RequiredMethodCallException(this.width.name);
  }

  private requireSortKey(): void {
    if (!this._sortable) {
      return;
    }

    if (!isNil(this._sortKey)) {
      return;
    }

    throw new RequiredMethodCallException(this.sortKey.name);
  }

  private requireFilterKey(): void {
    if (!this._filterable) {
      return;
    }

    if (!isNil(this._filterKey)) {
      return;
    }

    throw new RequiredMethodCallException(this.filterKey.name);
  }

  private configureDefaultType(): void {
    if (!isNil(this._type)) {
      return;
    }

    this.type(TableColumnType.Text);
  }

  private isCurrency(): boolean {
    return this._type === TableColumnType.Currency;
  }

  private configureDefaultSortable(): void {
    if (!isNil(this._sortable)) {
      return;
    }

    this.sortable(false);
  }

  private configureDefaultFilterable(): void {
    if (!isNil(this._filterable)) {
      return;
    }

    this.filterable(false);
  }

  private configureDefaultFrozen(): void {
    if (!isNil(this._frozen)) {
      return;
    }

    this.frozen(false);
  }
}
